<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  campaigns: {
    type: Array,
    required: true,
  },
  isLiveChatType: {
    type: Boolean,
    default: false,
  },
  isEditable: {
    type: Boolean,
    default: false,
  },
  isReportable: {
    type: Boolean,
    default: false,
  },
  showTotalClicks: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['edit', 'delete', 'duplicate', 'report']);
const { t } = useI18n();

const STATUS_COMPLETED = 'completed';
const STATUS_PROCESSING = 'processing';
const MESSAGE_PREVIEW_LIMIT = 88;
const failedPreviewIds = ref({});

const handleEdit = campaign => emit('edit', campaign);
const handleDelete = campaign => emit('delete', campaign);
const handleDuplicate = campaign => emit('duplicate', campaign);
const handleReport = campaign => emit('report', campaign);

const editButtonIcon = computed(() =>
  props.isLiveChatType ? 'i-lucide-sliders-vertical' : 'i-lucide-pencil'
);

const campaignStatus = campaign => {
  if (props.isLiveChatType) {
    return campaign.enabled
      ? t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.ENABLED')
      : t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.DISABLED');
  }

  if (campaign.campaign_status === STATUS_COMPLETED) {
    return t('CAMPAIGN.SMS.CARD.STATUS.COMPLETED');
  }

  if (campaign.campaign_status === STATUS_PROCESSING) {
    return t('CAMPAIGN.SMS.CARD.STATUS.SENDING');
  }

  return t('CAMPAIGN.SMS.CARD.STATUS.SCHEDULED');
};

const statusClass = campaign => {
  if (props.isLiveChatType) {
    return campaign.enabled
      ? 'status-enabled text-n-teal-11'
      : 'status-disabled text-n-slate-11';
  }

  if (campaign.campaign_status === STATUS_COMPLETED) {
    return 'status-disabled text-n-slate-11';
  }

  if (campaign.campaign_status === STATUS_PROCESSING) {
    return 'status-processing text-n-amber-11';
  }

  return 'status-enabled text-n-teal-11';
};

const scheduleLabel = campaign => {
  if (props.isLiveChatType) return '—';
  if (!campaign?.scheduled_at) return '—';

  const date = new Date(campaign.scheduled_at * 1000);
  if (Number.isNaN(date.getTime())) return '—';

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

const normalizedMessage = message =>
  (message || '')
    .toString()
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const compactCampaignMessage = message => {
  const text = normalizedMessage(message);
  if (!text) return '—';

  return text.length > MESSAGE_PREVIEW_LIMIT
    ? `${text.slice(0, MESSAGE_PREVIEW_LIMIT).trim()}...`
    : text;
};

const normalizeType = type => {
  const normalized = (type || '')
    .toString()
    .trim()
    .toLowerCase()
    .replaceAll(' ', '_');
  if (normalized === 'photo') return 'image';
  return normalized;
};

const previewFromMediaGroupBlock = block => {
  const imageItem = Array.isArray(block?.items)
    ? block.items.find(item =>
        ['image', 'photo'].includes(normalizeType(item?.type))
      )
    : null;
  return imageItem?.media_url || imageItem?.media || '';
};

const previewFromCarouselBlock = block => {
  const imageCard = Array.isArray(block?.cards)
    ? block.cards.find(card =>
        ['image', 'photo'].includes(
          normalizeType(card?.media_type || card?.type)
        )
      )
    : null;
  return imageCard?.media_url || imageCard?.media || '';
};

const previewFromPaidMediaBlock = block => {
  const imageItem = Array.isArray(block?.items)
    ? block.items.find(item =>
        ['image', 'photo'].includes(normalizeType(item?.type))
      )
    : null;
  return imageItem?.media_url || imageItem?.media || '';
};

const previewFromBlock = block => {
  const blockType = normalizeType(block?.type);
  if (blockType === 'image') return block?.media_url || block?.image_url || '';
  if (blockType === 'media_group') return previewFromMediaGroupBlock(block);
  if (blockType === 'carousel') return previewFromCarouselBlock(block);
  if (blockType === 'paid_media') return previewFromPaidMediaBlock(block);
  return '';
};

const extractPreviewImage = campaign => {
  const telegramData = campaign?.template_params?.telegram;
  if (!telegramData || typeof telegramData !== 'object') return '';

  if (telegramData.image_url) return telegramData.image_url;

  const blocks = Array.isArray(telegramData.blocks) ? telegramData.blocks : [];
  return blocks.map(previewFromBlock).find(Boolean) || '';
};

const previewImageCache = computed(() => {
  const cache = {};
  (props.campaigns || []).forEach(campaign => {
    cache[campaign.id] = extractPreviewImage(campaign);
  });
  return cache;
});

const previewImageFor = campaign =>
  failedPreviewIds.value[campaign.id]
    ? ''
    : previewImageCache.value[campaign.id];

const onPreviewImageError = campaignId => {
  failedPreviewIds.value = { ...failedPreviewIds.value, [campaignId]: true };
};

const canOpenReport = campaign =>
  props.isReportable &&
  !props.isLiveChatType &&
  campaign?.campaign_status === STATUS_COMPLETED;

const canEditCampaign = campaign => {
  if (props.isLiveChatType) return true;
  if (!props.isEditable) return false;
  return ![STATUS_COMPLETED, STATUS_PROCESSING].includes(
    campaign?.campaign_status
  );
};

const totalClickCount = campaign => {
  const analytics = campaign?.template_params?.telegram?.analytics || {};
  const clicks = Number(analytics.total_clicks ?? analytics.click_count ?? 0);
  return Number.isFinite(clicks) && clicks >= 0 ? clicks : 0;
};

const handleRowClick = campaign => {
  if (!canOpenReport(campaign)) return;
  handleReport(campaign);
};
</script>

<template>
  <div class="campaigns-table-wrap">
    <table class="campaigns-table w-full">
      <thead>
        <tr class="table-head-row">
          <th class="px-6 py-3 text-left">Campaign</th>
          <th class="px-6 py-3 text-left">Status</th>
          <th class="px-6 py-3 text-left">Inbox</th>
          <th class="px-6 py-3 text-left">Schedule</th>
          <th v-if="showTotalClicks" class="px-6 py-3 text-left">
            Total clicks
          </th>
          <th class="px-6 py-3 text-right">Actions</th>
        </tr>
      </thead>
      <tbody class="table-body">
        <tr
          v-for="campaign in campaigns"
          :key="campaign.id"
          class="table-row"
          :class="{ 'cursor-pointer': canOpenReport(campaign) }"
          @click="handleRowClick(campaign)"
        >
          <td class="px-6 py-4 min-w-0">
            <div
              class="flex items-start min-w-0"
              :class="isEditable ? 'gap-3' : 'gap-0'"
            >
              <div v-if="isEditable" class="campaign-thumb-wrap flex-shrink-0">
                <img
                  v-if="previewImageFor(campaign)"
                  :src="previewImageFor(campaign)"
                  class="campaign-thumb"
                  alt=""
                  loading="lazy"
                  decoding="async"
                  fetchpriority="low"
                  @error="onPreviewImageError(campaign.id)"
                />
                <span v-else class="campaign-thumb-fallback i-ph-image-bold" />
              </div>
              <div class="flex flex-col gap-1 min-w-0 flex-1">
                <span
                  class="campaign-title font-semibold text-n-slate-12 truncate"
                >
                  {{ campaign.title }}
                </span>
                <span class="campaign-message text-sm text-n-slate-11 truncate">
                  {{ compactCampaignMessage(campaign.message) }}
                </span>
              </div>
            </div>
          </td>
          <td class="px-6 py-4">
            <span
              class="status-badge inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold"
              :class="statusClass(campaign)"
            >
              {{ campaignStatus(campaign) }}
            </span>
          </td>
          <td class="px-6 py-4">
            <span class="text-sm text-n-slate-12">
              {{ campaign.inbox?.name || '—' }}
            </span>
          </td>
          <td class="px-6 py-4">
            <span class="text-sm text-n-slate-11">
              {{ scheduleLabel(campaign) }}
            </span>
          </td>
          <td v-if="showTotalClicks" class="px-6 py-4">
            <span class="text-sm text-n-slate-12">
              {{ totalClickCount(campaign).toLocaleString() }}
            </span>
          </td>
          <td class="px-6 py-4">
            <div class="flex justify-end gap-2">
              <Button
                v-if="isEditable"
                variant="faded"
                size="sm"
                color="slate"
                icon="i-ph-copy-bold"
                @click.stop="handleDuplicate(campaign)"
              />
              <Button
                v-if="canEditCampaign(campaign)"
                variant="faded"
                size="sm"
                color="slate"
                :icon="editButtonIcon"
                @click.stop="handleEdit(campaign)"
              />
              <Button
                variant="faded"
                color="ruby"
                size="sm"
                icon="i-lucide-trash"
                @click.stop="handleDelete(campaign)"
              />
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped>
.campaigns-table {
  border-collapse: collapse;
  background: #ffffff;
}

.table-head-row {
  background-color: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.table-body {
  background: #ffffff;
}

.table-row {
  border-bottom: 1px solid #f3f4f6;
  transition: background-color 0.1s ease;
}

.table-row:hover {
  background-color: #f9fafb;
}

.table-row:last-child {
  border-bottom: none;
}

.status-badge {
  transition: all 0.15s ease;
}

.status-enabled {
  background-color: rgba(20, 184, 166, 0.12);
  color: #0f766e;
}

.status-disabled {
  background-color: rgba(107, 114, 128, 0.1);
  color: #374151;
}

.status-processing {
  background-color: rgba(245, 158, 11, 0.12);
  color: #92400e;
}

.campaign-thumb-wrap {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  background: #f3f4f6;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
}

.campaign-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.campaign-thumb-fallback {
  font-size: 13px;
  color: #9ca3af;
}

.campaign-title,
.campaign-message {
  max-width: min(56vw, 540px);
}

.campaign-title {
  color: #111827;
  font-size: 13px;
}

.campaign-message {
  color: #6b7280;
  font-size: 12px;
}
</style>
